import { useEffect, useRef, useState } from 'react';

const representationOptions = [
   { value: 'cartoon', label: 'Cartoon' },
   { value: 'ball-and-stick', label: 'Ball and Stick' },
   { value: 'spacefill', label: 'Spacefill' },
   { value: 'gaussian-surface', label: 'Gaussian Surface' },
];

export type StructureComponentType = 'pocket' | 'chain' | 'ligand';
export type Representation = 'cartoon' | 'ball-and-stick' | 'gaussian-surface' | 'gaussian-volume';
export const defaultPocketRepresentation: Representation = 'ball-and-stick';
export const defaultRestRepresentation: Representation = 'cartoon';
export const defaultLigandRepresentation: Representation = 'ball-and-stick';

export interface StructureRepresentationComponentProps {
   selectedRepresentation: Representation;
   changeRepresentation: (type: Representation) => void;
   title: string;
   showSelectedRepresentation?: boolean;
}

/**
 * A dropdown button for selecting a Molstar representation type (Cartoon,
 * Ball-and-Stick, Spacefill, or Gaussian Surface). The dropdown closes when the
 * user clicks outside it (handled via a document-level `mousedown` listener).
 *
 * When `showSelectedRepresentation` is `true` the button label reflects the
 * currently selected value; otherwise only the `title` is shown.
 *
 * Used inside `StructureRepresentationControlComponent` for chain, pocket, and
 * ligand representation selectors.
 */
const StructureRepresentationComponent = (props: StructureRepresentationComponentProps): React.JSX.Element => {
   const [open, setOpen] = useState(false);
   const ref = useRef<HTMLDivElement>(null);

   useEffect(() => {
      const handler = (e: MouseEvent): void => {
         if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return (): void => document.removeEventListener('mousedown', handler);
   }, []);

   const currentLabel =
      representationOptions.find((o) => o.value === props.selectedRepresentation)?.label ??
      props.selectedRepresentation;

   return (
      <div className="repr-dropdown" ref={ref}>
         <button className="repr-dropdown-btn" onClick={(): void => setOpen((o) => !o)}>
            <span className="repr-dropdown-title">{props.title}</span>
            {props.showSelectedRepresentation && (
               <>
                  <span className="repr-dropdown-sep">:</span>
                  <span className="repr-dropdown-value">{currentLabel}</span>
               </>
            )}
         </button>
         {open && (
            <div className="repr-dropdown-menu">
               {representationOptions.map((opt) => (
                  <button
                     key={opt.value}
                     className={`repr-dropdown-item${opt.value === props.selectedRepresentation ? ' active' : ''}`}
                     onClick={(): void => {
                        props.changeRepresentation(opt.value as Representation);
                        setOpen(false);
                     }}
                  >
                     {opt.label}
                  </button>
               ))}
            </div>
         )}
      </div>
   );
};

export default StructureRepresentationComponent;
